import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import type {
  MemberCommunicationDto,
  MemberCommunicationTargetOptionDto,
  MemberCommunicationTargetType
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import {
  completeAdminCommunicationAttachmentUpload,
  createAdminCommunicationAttachment,
  createAdminCommunicationAttachmentUploadSession,
  createAdminMemberCommunication,
  fetchAdminMemberCommunicationTargetOptions,
  fetchAdminMemberCommunications,
  hideAdminMemberCommunication,
  patchAdminMemberCommunication,
  republishAdminMemberCommunication
} from "../../shared/api/memberCommunication";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { DEFAULT_TENANT_TIMEZONE, formatTenantDateTime } from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { MemberCommunicationCard } from "../../shared/ui/MemberCommunicationCard";
import {
  buildDefaultCommunicationCategories,
  MemberCommunicationComposer
} from "../../shared/ui/MemberCommunicationComposer";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/events", label: "イベント" }
];

type TargetOptionsByType = Record<Exclude<MemberCommunicationTargetType, "tenant">, MemberCommunicationTargetOptionDto[]>;

type EditState = {
  title: string;
  body: string;
  publishFrom: string;
  publishTo: string;
};

function toDatetimeLocalValue(value: string | null): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toIsoString(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

function buildEditStateMap(communications: readonly MemberCommunicationDto[]): Record<string, EditState> {
  return Object.fromEntries(
    communications.map((communication) => [
      communication.communicationId,
      {
        title: communication.title ?? "",
        body: communication.body ?? "",
        publishFrom: toDatetimeLocalValue(communication.publishFrom),
        publishTo: toDatetimeLocalValue(communication.publishTo)
      }
    ])
  );
}

function statusLabel(status: string): string {
  if (status === "draft") return "下書き";
  if (status === "scheduled") return "予約中";
  if (status === "published") return "公開中";
  if (status === "hidden") return "非公開";
  if (status === "expired") return "公開終了";
  if (status === "deleted") return "削除済み";
  return describeStatus(status);
}

export function AdminMemberCommunicationsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [communications, setCommunications] = useState<{ items: MemberCommunicationDto[] } | null>(null);
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [actionState, setActionState] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [targetType, setTargetType] = useState<MemberCommunicationTargetType>("tenant");
  const [targetId, setTargetId] = useState("");
  const [publishFrom, setPublishFrom] = useState("");
  const [publishTo, setPublishTo] = useState("");
  const [targetOptions, setTargetOptions] = useState<TargetOptionsByType>({
    lesson: [],
    occurrence: [],
    event: []
  });
  const [targetOptionsState, setTargetOptionsState] = useState<"idle" | "loading" | "error">("idle");
  const [targetOptionsError, setTargetOptionsError] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const selectableTargetType = targetType === "tenant" ? null : targetType;
  const selectedTargetOption = selectableTargetType ? targetOptions[selectableTargetType].find((option) => option.targetId === targetId) ?? null : null;

  async function refreshCommunications() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const nextCommunications = await fetchAdminMemberCommunications(tenantSlug);
      setCommunications(nextCommunications);
      setEdits(buildEditStateMap(nextCommunications.items));
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, "連絡事項の取得に失敗しました"));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }
    void refreshCommunications();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }
    let disposed = false;

    async function refreshTargetOptions() {
      setTargetOptionsState("loading");
      setTargetOptionsError(null);
      try {
        const [lessonOptions, occurrenceOptions, eventOptions] = await Promise.all([
          fetchAdminMemberCommunicationTargetOptions(tenantSlug, "lesson"),
          fetchAdminMemberCommunicationTargetOptions(tenantSlug, "occurrence"),
          fetchAdminMemberCommunicationTargetOptions(tenantSlug, "event")
        ]);
        if (disposed) {
          return;
        }
        setTargetOptions({
          lesson: lessonOptions.items,
          occurrence: occurrenceOptions.items,
          event: eventOptions.items
        });
        setTargetOptionsState("idle");
      } catch (error) {
        if (disposed) {
          return;
        }
        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }
        setTargetOptionsState("error");
        setTargetOptionsError(getProtectedApiErrorMessage(error, "対象候補の取得に失敗しました"));
      }
    }

    void refreshTargetOptions();

    return () => {
      disposed = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  useEffect(() => {
    if (!selectableTargetType) {
      setTargetId("");
      return;
    }
    const options = targetOptions[selectableTargetType];
    if (options.length === 0) {
      return;
    }
    if (!targetId || !options.some((option) => option.targetId === targetId)) {
      setTargetId(options[0]?.targetId ?? "");
    }
  }, [selectableTargetType, targetId, targetOptions]);

  async function handlePatch(communicationId: string) {
    const edit = edits[communicationId];
    if (!edit) {
      return;
    }
    setActionState(`patch:${communicationId}`);
    setLoadError(null);
    setFeedbackMessage(null);
    try {
      await patchAdminMemberCommunication(tenantSlug, communicationId, {
        title: edit.title || null,
        body: edit.body || null,
        publishFrom: toIsoString(edit.publishFrom),
        publishTo: toIsoString(edit.publishTo)
      });
      setFeedbackMessage("連絡事項を保存しました。");
      await refreshCommunications();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setLoadError(getProtectedApiErrorMessage(error, "連絡事項の保存に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  async function handleHide(communicationId: string) {
    setActionState(`hide:${communicationId}`);
    setLoadError(null);
    setFeedbackMessage(null);
    try {
      await hideAdminMemberCommunication(tenantSlug, communicationId);
      setFeedbackMessage("連絡事項を非公開にしました。");
      await refreshCommunications();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setLoadError(getProtectedApiErrorMessage(error, "連絡事項の非公開処理に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  async function handleRepublish(communicationId: string) {
    setActionState(`republish:${communicationId}`);
    setLoadError(null);
    setFeedbackMessage(null);
    try {
      await republishAdminMemberCommunication(tenantSlug, communicationId);
      setFeedbackMessage("連絡事項を再公開しました。");
      await refreshCommunications();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setLoadError(getProtectedApiErrorMessage(error, "連絡事項の再公開に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="連絡事項"
      description={`クラブ全体や対象の${tenantLabels.lesson}・予定・イベントへ連絡を送信します。`}
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>連絡事項を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}
        {targetOptionsError ? <p>{targetOptionsError}</p> : null}

        <section style={{ display: "grid", gap: 12, border: "1px solid #e5e5e5", borderRadius: 12, padding: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>メンバーへ連絡</h2>
          <label style={{ display: "grid", gap: 4 }}>
            <span>対象</span>
            <select value={targetType} onChange={(event: ChangeEvent<HTMLSelectElement>) => setTargetType(event.currentTarget.value as MemberCommunicationTargetType)}>
              <option value="tenant">クラブ全体</option>
              <option value="lesson">{tenantLabels.lesson}</option>
              <option value="occurrence">個別予定</option>
              <option value="event">イベント</option>
            </select>
          </label>
          {selectableTargetType ? (
            <label style={{ display: "grid", gap: 4 }}>
              <span>対象を選択</span>
              {targetOptions[selectableTargetType].length > 0 ? (
                <select value={targetId} onChange={(event) => setTargetId(event.currentTarget.value)}>
                  {targetOptions[selectableTargetType].map((option) => (
                    <option key={`${option.targetType}:${option.targetId}`} value={option.targetId}>
                      {option.label}
                      {option.subtitle ? ` / ${option.subtitle}` : ""}
                      {option.status ? ` / ${statusLabel(option.status)}` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <input value={targetId} onChange={(event) => setTargetId(event.currentTarget.value)} />
              )}
            </label>
          ) : null}
          {targetOptionsState === "loading" ? <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>対象候補を読み込んでいます。</p> : null}
          {selectedTargetOption ? (
            <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
              {selectedTargetOption.subtitle ?? "対象"}
              {selectedTargetOption.status ? ` / ${statusLabel(selectedTargetOption.status)}` : ""}
              {selectedTargetOption.scheduledAt ? ` / ${formatTenantDateTime(selectedTargetOption.scheduledAt, DEFAULT_TENANT_TIMEZONE)}` : ""}
            </p>
          ) : null}
          <label style={{ display: "grid", gap: 4 }}>
            <span>公開開始（任意）</span>
            <input type="datetime-local" value={publishFrom} onChange={(event) => setPublishFrom(event.currentTarget.value)} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>公開終了（任意）</span>
            <input type="datetime-local" value={publishTo} onChange={(event) => setPublishTo(event.currentTarget.value)} />
          </label>
          <MemberCommunicationComposer
            api={{
              createCommunication: (request) => createAdminMemberCommunication(tenantSlug, request),
              createAttachment: (communicationId, request) => createAdminCommunicationAttachment(tenantSlug, communicationId, request),
              createUploadSession: (communicationId, attachmentId) => createAdminCommunicationAttachmentUploadSession(tenantSlug, communicationId, attachmentId),
              completeUpload: (communicationId, attachmentId, request) => completeAdminCommunicationAttachmentUpload(tenantSlug, communicationId, attachmentId, request)
            }}
            categories={buildDefaultCommunicationCategories(tenantLabels)}
            defaultCategory="general"
            submitLabel="メンバーへ連絡"
            disabled={targetOptionsState === "loading" || Boolean(selectableTargetType && !targetId.trim())}
            titleLabel="タイトル（任意）"
            bodyLabel="連絡内容（任意）"
            buildRequest={(values, attachmentType) => ({
              title: values.title || null,
              body: values.body || null,
              category: values.category,
              targetType,
              targetId: targetType === "tenant" ? null : targetId.trim(),
              publishFrom: toIsoString(publishFrom),
              publishTo: toIsoString(publishTo),
              attachmentType
            })}
            onSuccess={async () => {
              setPublishFrom("");
              setPublishTo("");
              await refreshCommunications();
            }}
          />
        </section>

        <section style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>連絡事項一覧</h2>
          {communications?.items.length === 0 ? <p style={{ margin: 0 }}>連絡事項はありません。</p> : null}
          {communications?.items.map((communication) => (
            <article key={communication.communicationId} style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 12 }}>
              <MemberCommunicationCard communication={communication} tenantSlug={tenantSlug} thumbnailSurface="admin" labels={tenantLabels} />
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                公開状態: {statusLabel(communication.publishStatus)} / 更新: {formatTenantDateTime(communication.updatedAt, DEFAULT_TENANT_TIMEZONE)}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                公開期間: {communication.publishFrom ? formatTenantDateTime(communication.publishFrom, DEFAULT_TENANT_TIMEZONE) : "未設定"}
                {" "}〜 {communication.publishTo ? formatTenantDateTime(communication.publishTo, DEFAULT_TENANT_TIMEZONE) : "未設定"}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={() => void handleHide(communication.communicationId)} disabled={actionState === `hide:${communication.communicationId}`}>
                  {actionState === `hide:${communication.communicationId}` ? "処理中..." : "非公開にする"}
                </button>
                <button type="button" onClick={() => void handleRepublish(communication.communicationId)} disabled={actionState === `republish:${communication.communicationId}`}>
                  {actionState === `republish:${communication.communicationId}` ? "処理中..." : "再公開"}
                </button>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                <strong>編集</strong>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>タイトル</span>
                  <input
                    value={edits[communication.communicationId]?.title ?? ""}
                    onChange={(event) => setEdits((current) => ({
                      ...current,
                      [communication.communicationId]: {
                        ...(current[communication.communicationId] ?? { title: "", body: "", publishFrom: "", publishTo: "" }),
                        title: event.currentTarget.value
                      }
                    }))}
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>連絡内容</span>
                  <textarea
                    rows={3}
                    value={edits[communication.communicationId]?.body ?? ""}
                    onChange={(event) => setEdits((current) => ({
                      ...current,
                      [communication.communicationId]: {
                        ...(current[communication.communicationId] ?? { title: "", body: "", publishFrom: "", publishTo: "" }),
                        body: event.currentTarget.value
                      }
                    }))}
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>公開開始</span>
                  <input
                    type="datetime-local"
                    value={edits[communication.communicationId]?.publishFrom ?? ""}
                    onChange={(event) => setEdits((current) => ({
                      ...current,
                      [communication.communicationId]: {
                        ...(current[communication.communicationId] ?? { title: "", body: "", publishFrom: "", publishTo: "" }),
                        publishFrom: event.currentTarget.value
                      }
                    }))}
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>公開終了</span>
                  <input
                    type="datetime-local"
                    value={edits[communication.communicationId]?.publishTo ?? ""}
                    onChange={(event) => setEdits((current) => ({
                      ...current,
                      [communication.communicationId]: {
                        ...(current[communication.communicationId] ?? { title: "", body: "", publishFrom: "", publishTo: "" }),
                        publishTo: event.currentTarget.value
                      }
                    }))}
                  />
                </label>
                <button type="button" onClick={() => void handlePatch(communication.communicationId)} disabled={actionState === `patch:${communication.communicationId}`}>
                  {actionState === `patch:${communication.communicationId}` ? "保存中..." : "編集を保存"}
                </button>
              </div>
            </article>
          ))}
        </section>
      </div>
    </OperatorPageScaffold>
  );
}
